import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Product, Category } from '../types';
import AddToCartButton from '../components/AddToCartButton';
import FixedAddToCartButton from '../components/FixedAddToCartButton';
import FavoriteButton from '../components/FavoriteButton';
import ProductCard from '../components/ProductCard';
import RecentlyViewed from '../components/RecentlyViewed';
import LoadingSpinner from '../components/LoadingSpinner';
import SEO from '../components/SEO';
import { ProductStructuredData, OrganizationStructuredData } from '../components/StructuredData';
import { fetchProductById, fetchProductByHandle, fetchProducts, fetchCategoryBySlug } from '../services/api';
import { productImage, proxiedImage, onImgError } from '../utils/images';
import { useRecentlyViewed } from '../hooks/useRecentlyViewed';
import { useSiteConfig } from '../hooks/useConfig';
import { getIngredientIntel } from '../data/ingredientIntel';

const ProductPage: React.FC = () => {
  const { id } = useParams<{ id: string; slug?: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState<Product[]>([]);
  const addRef = useRef<HTMLDivElement>(null);
  const { record } = useRecentlyViewed();
  const { complianceMode } = useSiteConfig();

  useEffect(() => {
    let active = true; setLoading(true);
    (async () => {
      if (!id) { setLoading(false); return; }
      const numeric = /^\d+$/.test(id);
      const p = numeric ? await fetchProductById(id) : await fetchProductByHandle(id);
      if (!active) return;
      setProduct(p);
      if (p) {
        record({ id: String(p.id), name: p.name, image: p.image || '', price: p.price, handle: (p as any).handle });
        // Canonicalize to the handle URL (Shopify-style) when we arrived by id
        if ((p as any).handle && id !== (p as any).handle) {
          window.history.replaceState({}, '', `/products/${(p as any).handle}`);
        }
        const [cat, all] = await Promise.all([fetchCategoryBySlug(p.category), fetchProducts({ category: p.category })]);
        if (!active) return;
        setCategory(cat);
        setRelated(all.filter((x) => x.id !== p.id).slice(0, 4));
      }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [id, record]);

  if (loading) return <div className="container-custom py-28 flex justify-center"><LoadingSpinner /></div>;

  if (!product) return (
    <div className="container-custom py-24 text-center">
      <h1 className="mb-4">Product not found</h1>
      <p className="text-earth-600 mb-8">The product you're looking for doesn't exist.</p>
      <Link to="/search" className="btn btn-primary">Browse all products</Link>
    </div>
  );

  const hasDesc = product.description && !/^(Imported from Amazon ASIN|Newly imported listing)/i.test(product.description);
  const handle = (product as any).handle || String(product.id);
  const canonicalUrl = `https://goodcart.org/products/${handle}`;
  const intel = getIngredientIntel(product.category);
  const hasIngredients = product.ingredients && product.ingredients.length > 0;
  const hasBenefits = product.benefits && product.benefits.length > 0;
  const showWhyGood = hasIngredients || hasBenefits || intel.avoid.length > 0;

  return (
    <div className="py-10 md:py-12">
      <SEO title={product.name} description={(hasDesc ? product.description : `${product.name}, a health-conscious pick on GoodCart.`).substring(0, 160)}
        image={`/og/product/${handle}.png`} url={canonicalUrl} type="product" siteName="GoodCart" />
      <ProductStructuredData product={product} url={canonicalUrl} />
      <OrganizationStructuredData />

      <div className="container-custom">
        <nav className="mb-8">
          <ol className="flex items-center gap-2 text-sm text-earth-500 flex-wrap">
            <li><Link to="/" className="hover:text-sage-700">Home</Link></li>
            {category && (<><li>/</li><li><Link to={`/categories/${category.slug}`} className="hover:text-sage-700">{category.name}</Link></li></>)}
            <li>/</li>
            <li className="text-earth-900 font-medium truncate max-w-[16rem]">{product.name}</li>
          </ol>
        </nav>

        <div className="organic-card p-5 md:p-8 mb-10">
          <div className="grid md:grid-cols-2 gap-8 md:gap-10">
            <motion.div initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
              <ProductGallery product={product} />
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
              {category && <Link to={`/categories/${category.slug}`} className="eyebrow mb-3">{category.name}</Link>}
              <h1 className="mt-2 mb-4">{product.name}</h1>

              <div className="flex flex-wrap items-center gap-2 mb-5">
                {product.healthScore > 0 && <span className="chip bg-sage-600 text-cream-50 border-sage-700">Health score {product.healthScore}</span>}
                {product.rating > 0 && <span className="chip">{product.rating.toFixed(1)} ★</span>}
                {product.tags && product.tags.map((t, i) => <span key={i} className="chip">{t}</span>)}
              </div>

              {hasDesc && <p className="text-earth-700 mb-6">{product.description}</p>}

              <div className="mb-5">
                {complianceMode ? (
                  <span className="text-earth-500 text-base font-medium">See current price on Amazon</span>
                ) : product.price > 0 ? (
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="text-2xl font-semibold text-sage-700 tabular-nums">${product.price.toFixed(2)}</span>
                    {product.listPrice && product.listPrice > product.price && (
                      <>
                        <span className="text-earth-400 line-through tabular-nums">${product.listPrice.toFixed(2)}</span>
                        <span className="rounded-full bg-clay-500 text-cream-50 text-xs font-semibold px-2.5 py-1">
                          Save {Math.round((1 - product.price / product.listPrice) * 100)}%
                        </span>
                      </>
                    )}
                  </div>
                ) : (
                  <span className="text-earth-500 text-base font-medium">See current price at checkout</span>
                )}
              </div>

              <div ref={addRef} className="flex flex-col sm:flex-row gap-3">
                <AddToCartButton product={product} className="w-full sm:w-auto" />
                {product.amazonLink && (
                  <a href={product.amazonLink} target="_blank" rel="noopener noreferrer" className="btn btn-outline w-full sm:w-auto">Check price</a>
                )}
              </div>

              <p className="mt-6 text-xs text-earth-500 leading-relaxed">
                {complianceMode
                  ? 'As an Amazon Associate, GoodCart earns from qualifying purchases. Prices and availability shown here may differ from the live listing; the price at checkout applies.'
                  : 'GoodCart earns a commission from qualifying purchases made through outbound links, at no extra cost to you. Prices and shipping are calculated at checkout.'}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Why it's good: what's in it vs what you avoid */}
        {showWhyGood && (
          <div className="grid md:grid-cols-2 gap-5 md:gap-6 mb-14">
            <motion.div
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
              className="rounded-2xl bg-sage-50 border border-sage-200 p-6"
            >
              <h2 className="font-display text-xl text-sage-800 mb-1">Why it's a good pick</h2>
              <p className="text-sm text-sage-700/80 mb-4">What's in this product, and why it earns its place here.</p>
              {hasIngredients && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-sage-800 mb-1.5">Ingredients</h3>
                  <p className="text-sm text-earth-700 leading-relaxed">{product.ingredients!.join(', ')}</p>
                </div>
              )}
              {hasBenefits && (
                <div>
                  <h3 className="text-sm font-semibold text-sage-800 mb-1.5">Benefits</h3>
                  <ul className="space-y-1.5">
                    {product.benefits!.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-earth-700">
                        <svg className="h-4 w-4 mt-0.5 text-sage-600 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!hasIngredients && !hasBenefits && (
                <p className="text-sm text-earth-700">Vetted against our clean-ingredient standards before listing.</p>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.08 }}
              className="rounded-2xl bg-clay-50 border border-clay-200 p-6"
            >
              <h2 className="font-display text-xl text-earth-900 mb-1">What you avoid</h2>
              <p className="text-sm text-earth-600 mb-4">{intel.note}</p>
              <ul className="space-y-1.5">
                {intel.avoid.map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-earth-700">
                    <svg className="h-4 w-4 mt-0.5 text-clay-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    {a}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        )}

        {related.length > 0 && (
          <div className="mb-6">
            <h2 className="mb-6">You might also like</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {related.map((r, i) => (<ProductCard key={r.id} product={r} index={i} />))}
            </div>
          </div>
        )}

        <RecentlyViewed />

        {product && <FixedAddToCartButton product={product} originalButtonRef={addRef} />}
      </div>
    </div>
  );
};


// Swipeable image gallery: main square + thumbnail strip (mobile swipe, desktop click)
const ProductGallery: React.FC<{ product: Product }> = ({ product }) => {
  const images = React.useMemo(() => {
    const list = [productImage(product), ...(product.images || []).map(proxiedImage)].filter(Boolean);
    return Array.from(new Set(list));
  }, [product]);
  const [active, setActive] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);

  const goTo = (i: number) => {
    setActive(i);
    const track = trackRef.current;
    if (track) track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
  };

  return (
    <div>
      <div className="relative">
        <div
          ref={trackRef}
          onScroll={(e) => {
            const el = e.currentTarget;
            const i = Math.round(el.scrollLeft / el.clientWidth);
            if (i !== active) setActive(i);
          }}
          className="flex overflow-x-auto snap-x snap-mandatory rounded-2xl border border-earth-100 bg-white scrollbar-none"
          style={{ scrollbarWidth: 'none' }}
        >
          {images.map((src, i) => (
            <div key={i} className="aspect-square w-full flex-shrink-0 snap-center">
              <img
                src={src}
                alt={i === 0 ? product.name : `${product.name}, view ${i + 1}`}
                fetchPriority={i === 0 ? 'high' : undefined}
                loading={i === 0 ? undefined : 'lazy'}
                decoding="async"
                className="w-full h-full object-contain p-4"
                onError={onImgError()}
              />
            </div>
          ))}
        </div>
        <FavoriteButton productId={product.id} className="absolute top-4 left-4" />
        {images.length > 1 && (
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Image ${i + 1}`}
                onClick={() => goTo(i)}
                className={`h-1.5 rounded-full transition-all ${i === active ? 'w-5 bg-sage-600' : 'w-1.5 bg-earth-300'}`}
              />
            ))}
          </div>
        )}
      </div>
      {images.length > 1 && (
        <div className="hidden sm:flex gap-2 mt-3">
          {images.map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`View image ${i + 1}`}
              className={`h-16 w-16 rounded-lg border overflow-hidden bg-white transition-colors ${
                i === active ? 'border-sage-600 ring-1 ring-sage-500' : 'border-earth-200 hover:border-earth-300'
              }`}
            >
              <img src={src} alt="" loading="lazy" className="w-full h-full object-contain p-1" onError={onImgError()} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductPage;
